import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Tag,
  DatePicker,
  Tooltip,
  Avatar,
  Image,
  Select,
  Modal,
} from "antd";
import React, { useState, useContext, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import * as XLSX from "xlsx";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import moment from "moment";
import { useNavigate } from "react-router";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import ImportForm from "../../components/ImportForm";
import ConfirmationBox from "../../components/ConfirmationBox";
import { AuthContext } from "../../context/AuthContext";
import { USERTYPE } from "../../constants/conts";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import SubAdminForm from "./SubAdminForm";
import confirm from "antd/lib/modal/confirm";

const Search = Input.Search;
const { RangePicker } = DatePicker;

function AdminManager() {
  const sectionName = "User";
  const routeName = "sub-admin";

  const api = {
    status: apiPath.statusCustomer,
    delete: apiPath.deleteCustomer,
    addEdit: apiPath.addEditCustomer,
    // list: apiPath.listCustomer,
    importFile: apiPath.importCustomer,
  };

  const [searchText, setSearchText] = useState("");
  const { userProfile, userPermission } = useContext(AuthContext);
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [activityVisible, setActivityVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [isUpdate, setIsUpdate] = useState(false);
  const { Option } = Select;
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activityPagination, activitySetPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    status: null,
    role: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 300);
  const navigate = useNavigate();
  const isSuperAdmin = userProfile?.role === USERTYPE.SUPER_ADMIN;

  const isAddPermissionGranted =
    isSuperAdmin || userPermission?.CustomerSection?.includes("add");

  const isUpdatePermissionGranted =
    isSuperAdmin || userPermission?.CustomerSection?.includes("edit");

  const isViewPermissionGranted =
    isSuperAdmin || userPermission?.CustomerSection?.includes("view");

  const view = (id) => navigate(`/admin/admin-account-manager/view/${id}`);

  const fetchData = (pagination, filters = {}) => {
    setLoading(true);

    const params = new URLSearchParams();

    // 🔍 Search
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // 📅 Date filters
    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    // 🔃 Status
    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.role) {
      params.append("role", filters.role);
    }

    // 📄 Pagination
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `${apiPath.listAdmins}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);

        setList(data?.data?.data || []);
        setPagination({
          current: pagination.current,
          total: data?.data?.pagination?.total || 0,
          pageSize:
            Number(data?.data?.pagination?.limit) || pagination.pageSize,
        });
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const fetchActivityData = (pagination, id) => {
    setLoading(true);

    const params = new URLSearchParams();

    // 📄 Pagination
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `${apiPath.listLogActivities}/${id}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setActivityList(data?.data?.data || []);
        activitySetPagination({
          current: pagination.current,
          total: data?.data?.total || 0,
          pageSize: Number(data?.data?.limit) || pagination.pageSize,
        });
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChangeDate = (dates) => {
    if (!dates) {
      setStartDate();
      setEndDate();
    } else {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
    }
  };

  const confirmStatus = (id, status) => {
    setTimeout(() => {
      confirm({
        maskClosable: true,
        icon: <ExclamationCircleOutlined />,
        className: "tab_modal",
        cancelButtonProps: { color: "red" },
        content: (
          <p
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Are you sure you want change the status ?
          </p>
        ),
        onOk() {
          request({
            url: `${apiPath.updateStatus}/${id}`,
            method: "PUT",
            data: { status: status === "Active" ? "Inactive" : "Active" },
            onSuccess: (data) => {
              fetchData(pagination, filters);
              ShowToast("Status Changed Successfully", Severty.SUCCESS);
            },
            onError: (error) => {
              setLoading(false);
              ShowToast(error, Severty.ERROR);
            },
          });
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    // reset filters
    setFilters({
      status: null,
      role: null,
    });

    // reset search & dates
    setSearchText("");
    setStartDate(null);
    setEndDate(null);

    // reset pagination to first page
    const resetPagination = { ...pagination, current: 1 };

    // refetch data without filters
    fetchData(resetPagination, filters);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (_, { name, _id, profilePic }) => {
        const capitalizedName = name
          ? name.charAt(0).toUpperCase() + name.slice(1)
          : "Not Available";

        return (
          <>
            {!profilePic ? (
              <Avatar size={40}>{name ? name.charAt(0) : "N/A"}</Avatar>
            ) : (
              <Image className="image-index-radius" src={profilePic} />
            )}
            <a className="avatar-text" onClick={() => view(_id)}>
              {capitalizedName}
            </a>
          </>
        );
      },
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      width: 220,
      render: (email) => email || "-",
    },
    {
      title: "Mobile Number",
      index: "phoneNumber",
      render: (_, { phoneNumber, countryCode }) =>
        `${countryCode ? "+" + countryCode + "-" : ""}${phoneNumber || "-"}`,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => role,
    },
    {
      title: "Status",
      render: (_, { is_delete, status, _id }) =>
        !is_delete ? (
          <Tag
            style={{ cursor: "pointer" }}
            color={status === "Active" ? "green" : "red"}
            onClick={() => confirmStatus(_id, status)}
          >
            {status === "Active" ? "Active" : "Inactive"}
          </Tag>
        ) : (
          <Tag color="red">Account Deleted</Tag>
        ),
    },
    {
      title: "Registered On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => moment(createdAt).format("DD-MM-YYYY"),
    },
    {
      title: "Action",
       
      render: (_, record) => (
        <>
          {!record.is_delete && (
            <Tooltip title="Update Sub-Admin">
              <Button
                disabled={!isUpdatePermissionGranted}
                onClick={() => {
                  setSelected(record);
                  setVisible(true);
                  setIsUpdate(true);
                }}
              >
                <i className="fa fa-pen"></i>
              </Button>
            </Tooltip>
          )}

          <Tooltip title="View User">
            <Button
              disabled={!isViewPermissionGranted}
              onClick={() => view(record._id)}
            >
              <i className="fa fa-eye"></i>
            </Button>
          </Tooltip>

          <Tooltip title="Show Log Activity">
            <Button
              onClick={() => {
                setActivityVisible(true);
                fetchActivityData(activitySetPagination, record._id);
              }}
            >
              <i className="fa fa-solid fa-user"></i>
            </Button>
          </Tooltip>
        </>
      ),
    },
  ];

  const activityColumns = [
    {
      title: "Activity Name",
      dataIndex: "action",
      key: "action",
      width: 200,
      render: (_, { action, _id }) => {
        return (
          <>
            <a className="avatar-text">{action}</a>
          </>
        );
      },
    },
    {
      title: "Module Name",
      dataIndex: "module",
      width: 220,
      render: (module) => module || "-",
    },
    {
      title: "Activity Date",
      dataIndex: "createdAt",
      width: 220,
      render: (createdAt) => moment(createdAt).format("YYYY-MM-DD | HH:mm A"),
    },
  ];

  useEffect(() => {
    setLoading(true);
    fetchData(pagination, filters);
  }, [filters, refresh, debouncedSearchText, startDate, endDate]);
  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              title="Admin Manager"
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  searchContent={
                    <Search
                      allowClear
                      placeholder="Search by User Name, Mobile, Email"
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  }
                  rightContent={
                    <Button
                      disabled={!isAddPermissionGranted}
                      onClick={() => {
                        setIsUpdate(false);
                        setVisible(true);
                      }}
                    >
                      Add Sub-Admin
                    </Button>
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Status"
                    value={filters.status}
                    onChange={(value) => handleFilterChange("status", value)}
                  >
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">In-active</Option>
                  </Select>
                  <Select
                    placeholder="Role"
                    value={filters.role}
                    onChange={(value) => handleFilterChange("role", value)}
                  >
                    <Option value="MODERATOR">Moderator</Option>
                    <Option value="CONTENT_MANAGER">Content Manager</Option>
                  </Select>
                  <RangePicker
                    value={
                      startDate && endDate
                        ? [moment(startDate), moment(endDate)]
                        : null
                    }
                    disabledDate={(current) =>
                      current && current.isAfter(moment().endOf("day"))
                    }
                    onChange={handleChangeDate}
                  />
                  <Button icon={<UndoOutlined />} onClick={() => handleReset()}>
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <h4 className="text-right">{ShowTotal(pagination.total || 0)}</h4>
              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={pagination}
                onChange={fetchData}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <SubAdminForm
          sectionName={sectionName}
          path={api.addEdit}
          show={visible}
          hide={() => setVisible(false)}
          data={selected}
          refresh={() => setRefresh((p) => !p)}
          isUpdate={isUpdate}
        />
      )}

      {importVisible && (
        <ImportForm
          path={api.importFile}
          sectionName={sectionName}
          show={importVisible}
          hide={() => setImportVisible(false)}
          refresh={() => setRefresh((p) => !p)}
        />
      )}

      {/* Show Activity Logs */}
      <Modal
        open={activityVisible}
        width={700}
        title="Activity Logs"
        onCancel={() => setActivityVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Table
          loading={loading}
          columns={activityColumns}
          dataSource={activityList}
          pagination={activityPagination}
          onChange={fetchActivityData}
        />
      </Modal>
    </>
  );
}

export default AdminManager;
